import { useState } from 'react';

import type { CreateDrawerProps, CreateDrawerStep } from './createDrawer.types';
import { injectCreateDrawerStyles } from './createDrawerStyles';
import { DrawerBody } from './DrawerBody';
import { DrawerFooter } from './DrawerFooter';
import { DrawerHeader } from './DrawerHeader';

export type { CreateDrawerProps, CreateDrawerStep };

/**
 * CreateDrawer — guided create-drawer primitive ported from the Bonsai Bar
 * design system (the "Growth" pattern chosen in BON-115). A right-side drawer
 * that walks a short create flow in 2–5 steps:
 *   · solid header band (clay by default) with eyebrow, display title, and an
 *     optional motivational quote
 *   · a branch rail down the left — the progress line grows and completed
 *     steps sprout a leaf node
 *   · step content slides in (transform-only entrance)
 *   · footer with Cancel · Back · Continue / final CTA
 *
 * Token-driven (`--nb-*`) and dark-aware via the dashboards token set; injects
 * its own scoped stylesheet once. Position it yourself (e.g. against the right
 * edge over a scrim) — it fills the height of its container. Reusable across
 * venue + workshop create flows.
 */

export function CreateDrawer({
  title,
  eyebrow,
  quote,
  attribution,
  steps = [],
  step,
  defaultStep = 0,
  onStepChange,
  ctaLabel = 'Create',
  onCta,
  canContinue = true,
  continueAlwaysEnabled = false,
  onNavigate,
  renderFinalCta,
  onCancel,
  onClose,
  footNote,
  railNote,
  announcement,
  headerColor = 'var(--nb-clay-500)',
  width = 660,
  style,
  className,
}: CreateDrawerProps) {
  injectCreateDrawerStyles();
  const [innerStep, setInnerStep] = useState(defaultStep);
  const cur = step ?? innerStep;
  const go = (i: number) => {
    const next = Math.max(0, Math.min(steps.length - 1, i));
    if (next === cur) return;
    // Opt-in nav guard (B2): a consumer can validate / block the move.
    if (onNavigate && !onNavigate(next, cur)) return;
    if (step === undefined) setInnerStep(next);
    onStepChange?.(next);
  };

  return (
    <aside
      className={['nb-cdrawer', className].filter(Boolean).join(' ')}
      data-component="CreateDrawer"
      style={{ width, maxWidth: '100vw', ...style }}
    >
      <DrawerHeader
        title={title}
        eyebrow={eyebrow}
        quote={quote}
        attribution={attribution}
        headerColor={headerColor}
        onClose={onClose}
      />
      <DrawerBody
        steps={steps}
        cur={cur}
        go={go}
        railNote={railNote}
        announcement={announcement}
        onCta={onCta}
        canContinue={canContinue}
      />
      <DrawerFooter
        cur={cur}
        count={steps.length}
        ctaLabel={ctaLabel}
        canContinue={canContinue}
        continueAlwaysEnabled={continueAlwaysEnabled}
        renderFinalCta={renderFinalCta}
        onCancel={onCancel}
        onCta={onCta}
        go={go}
        footNote={footNote}
      />
    </aside>
  );
}
